// The admin data scope a screen operates in: the deployment's environment and the first ecosystem it
// serves, read from the deployment profile, and the operator's customer from the session.
import { useQuery } from '@tanstack/react-query';
import type {
  AdminDataScope,
  CustomerId,
  Ecosystem,
  PowerDeploymentProfile,
  SourceMode,
} from '@finnest/power-admin-contracts';
import {
  customerIdFromSession,
  INTERNAL_CUSTOMER_ID,
  INTERNAL_TENANT_ID,
  operatorCustomerIdQueryOptions,
} from '../auth/operator-session-query.ts';
import { DeploymentGate, ServedEcosystemsGate } from './deployment-gate.tsx';

const FIXED_SOURCE_MODE: SourceMode = 'live';

export const buildAdminScope = (input: {
  readonly profile: PowerDeploymentProfile;
  readonly ecosystem: Ecosystem;
  readonly customerId: CustomerId;
}): AdminDataScope => ({
  sourceMode: FIXED_SOURCE_MODE,
  environment: input.profile.environment,
  tenantId: INTERNAL_TENANT_ID,
  customerId: input.customerId,
  ecosystem: input.ecosystem,
});

// The signed-in operator's customer; the internal customer until the session answers with one.
export const useOperatorCustomerId = (): CustomerId => {
  const customerQuery = useQuery(operatorCustomerIdQueryOptions);
  return (
    {
      success: (): CustomerId =>
        customerQuery.data === undefined
          ? INTERNAL_CUSTOMER_ID
          : customerIdFromSession(customerQuery.data),
      error: (): CustomerId => INTERNAL_CUSTOMER_ID,
      pending: (): CustomerId => INTERNAL_CUSTOMER_ID,
    } satisfies Record<typeof customerQuery.status, () => CustomerId>
  )[customerQuery.status]();
};

const CustomerOf = ({
  children,
}: {
  readonly children: (customerId: CustomerId) => JSX.Element;
}): JSX.Element => children(useOperatorCustomerId());

/**
 * Renders `children` with the operator's customer and the deployment profile once it is read, for
 * screens that do not depend on an ecosystem and so stay available on a deployment that serves none.
 */
export function CustomerScopeGate({
  children,
}: {
  readonly children: (customerId: CustomerId, profile: PowerDeploymentProfile) => JSX.Element;
}): JSX.Element {
  return (
    <DeploymentGate>
      {(profile) => <CustomerOf>{(customerId) => children(customerId, profile)}</CustomerOf>}
    </DeploymentGate>
  );
}

const ScopeOf = ({
  profile,
  ecosystem,
  children,
}: {
  readonly profile: PowerDeploymentProfile;
  readonly ecosystem: Ecosystem;
  readonly children: (scope: AdminDataScope) => JSX.Element;
}): JSX.Element =>
  children(buildAdminScope({ profile, ecosystem, customerId: useOperatorCustomerId() }));

/**
 * Renders `children` with the admin data scope of this deployment and operator, for screens that
 * depend on an ecosystem.
 */
export function AdminScopeGate({
  children,
}: {
  readonly children: (scope: AdminDataScope) => JSX.Element;
}): JSX.Element {
  return (
    <ServedEcosystemsGate>
      {([ecosystem], profile) => (
        <ScopeOf ecosystem={ecosystem} profile={profile}>
          {children}
        </ScopeOf>
      )}
    </ServedEcosystemsGate>
  );
}
